import React, { useMemo, useState } from 'react';
import useGameStore from '../../store/gameStore';
import { Trophy, Wrench, ArrowUpDown, Filter } from 'lucide-react';
import { FLAG_TYPE_COLORS, POS_BADGE } from './CarteReseau';
import {
  VILLES, NB_TYPES_MAGASIN, magasinsParVille, statutVille, STATUTS_VILLE,
  bonusDominationPct, bonusReseauGeo, PALIERS_COUVERTURE, BONUS_CAPITALE_PCT,
} from '../../utils/villes';
import { getTypeMagasin, TYPES_MAGASIN, getLocalisation } from '../../utils/ouverture';
import { getPositionnement } from '../../utils/retail';
import { computeReseauEffects } from '../../utils/magasin';
import { bilanAmeliorations } from '../../utils/ameliorations';
import { evenementLocalActif } from '../../utils/evenementsVilles';
import { formatMoney } from '../../utils/format';

const ORDRE_STATUT = { presence: 1, etabli: 2, fort: 3, domination: 4 };

/* Synthèse du réseau : filtres (type, pays, ville), tri par colonne, statuts. */
export default function SyntheseReseau() {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const [typesFiltre, setTypesFiltre] = useState([]);
  const [paysFiltre, setPaysFiltre] = useState('tous');
  const [villeFiltre, setVilleFiltre] = useState('toutes');
  const [tri, setTri] = useState({ cle: 'nb', sens: -1 });

  const parVille = useMemo(() => magasinsParVille(retail), [retail]);
  const geo = useMemo(() => bonusReseauGeo(retail), [retail]);
  const bilan = useMemo(() => bilanAmeliorations(retail), [retail]);
  const contributions = useMemo(() => {
    const map = {};
    computeReseauEffects(retail, totalDays).detail.forEach(d => { if (d.magasin?.id) map[d.magasin.id] = d.contribution; });
    return map;
  }, [retail, totalDays]);
  // bilanAmeliorations.parMagasin suit l'ordre [principal, ...magasins]
  const amelParMag = useMemo(() => {
    const map = {};
    const ordre = [retail?.magasin, ...(retail?.magasins || [])];
    ordre.forEach((m, i) => { if (m) map[m.id || 'principal'] = bilan.parMagasin[i]; });
    return map;
  }, [retail, bilan]);

  const villesImplantees = VILLES.filter(v => parVille[v.id]?.length);
  const loyersMois = Object.values(parVille).flat().reduce((s, x) => s + (x.magasin.loyerMensuel || 0) + (x.magasin.chargesMensuelles || 0), 0);
  const caWebCumul = retail?.ecommerce?.stats?.caWebCumul || 0;
  const nbParType = useMemo(() => {
    const map = {};
    Object.values(parVille).flat().forEach(x => { const t = x.magasin.typeId; if (t) map[t] = (map[t] || 0) + 1; });
    return map;
  }, [parVille]);

  const toggleType = (t) => setTypesFiltre(f => (f.includes(t) ? f.filter(x => x !== t) : [...f, t]));
  const trier = (cle) => setTri(t => ({ cle, sens: t.cle === cle ? -t.sens : -1 }));

  const lignes = useMemo(() => {
    let rows = villesImplantees.map(v => {
      const tous = parVille[v.id];
      const mags = typesFiltre.length ? tous.filter(x => typesFiltre.includes(x.magasin.typeId)) : tous;
      const types = [...new Set(tous.map(x => x.magasin.typeId).filter(Boolean))];
      return {
        v,
        mags,
        types,
        st: statutVille(types.length),
        loyer: mags.reduce((s, x) => s + (x.magasin.loyerMensuel || 0) + (x.magasin.chargesMensuelles || 0), 0),
        contrib: mags.reduce((s, x) => s + (contributions[x.magasin.id] || 0), 0),
      };
    });
    rows = rows.filter(r => r.mags.length > 0);
    if (paysFiltre === 'france') rows = rows.filter(r => !r.v.international);
    if (paysFiltre === 'intl') rows = rows.filter(r => r.v.international);
    if (villeFiltre !== 'toutes') rows = rows.filter(r => r.v.id === villeFiltre);
    const { cle, sens } = tri;
    rows.sort((a, b) => {
      if (cle === 'ville') return a.v.nom.localeCompare(b.v.nom) * sens;
      if (cle === 'statut') return (ORDRE_STATUT[a.st] - ORDRE_STATUT[b.st]) * sens;
      if (cle === 'loyer') return (a.loyer - b.loyer) * sens;
      if (cle === 'contrib') return (a.contrib - b.contrib) * sens;
      return (a.mags.length - b.mags.length) * sens;
    });
    return rows;
  }, [villesImplantees, parVille, typesFiltre, paysFiltre, villeFiltre, tri, contributions]);

  const Th = ({ cle, children, align = 'left' }) => (
    <th className={`px-2 py-2 text-${align}`}>
      <button type="button" onClick={() => trier(cle)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-300 transition-colors ${tri.cle === cle ? 'text-accent' : ''}`}
        data-testid={`synthese-tri-${cle}`}>
        {children} <ArrowUpDown className="w-2.5 h-2.5" />
      </button>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="synthese-reseau">
      {/* Progression de couverture + paliers */}
      <div className="surface-card bg-card border border-ink-600 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-400" />
          <p className="text-sm font-black text-foreground">Notoriété du réseau</p>
          <span className="text-xs font-bold text-emerald-400" data-testid="synthese-bonus-total">bonus trafic actif : +{geo.totalPct} %</span>
          {geo.prochainPalier && (
            <span className="ml-auto text-[10px] text-slate-400">
              Prochain palier : {geo.prochainPalier.villes} villes → +{geo.prochainPalier.traficPct} %
            </span>
          )}
        </div>
        <div className="h-2.5 rounded-full bg-ink-800 border border-ink-600 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-accent to-amber-400 transition-all" style={{ width: `${Math.min(100, (geo.nbVilles / VILLES.length) * 100)}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 text-[10px]">
          <span className="text-slate-400" data-testid="synthese-nb-villes">{geo.nbVilles}/{VILLES.length} villes</span>
          {PALIERS_COUVERTURE.map(p => (
            <span key={p.villes} className={`px-1.5 py-0.5 rounded-full border font-bold ${geo.nbVilles >= p.villes ? 'border-emerald-500/60 text-emerald-300 bg-emerald-500/10' : 'border-ink-600 text-slate-500'}`}>
              {p.villes} villes → +{p.traficPct} %
            </span>
          ))}
          <span className={`px-1.5 py-0.5 rounded-full border font-bold ${geo.nbCapitales > 0 ? 'border-sky-500/60 text-sky-300 bg-sky-500/10' : 'border-ink-600 text-slate-500'}`}>
            +{BONUS_CAPITALE_PCT} % / capitale ({geo.nbCapitales})
          </span>
          <span className="px-1.5 py-0.5 rounded-full border border-amber-500/60 text-amber-300 bg-amber-500/10 font-bold">
            ★ Domination ({NB_TYPES_MAGASIN} formats) → +{bonusDominationPct(NB_TYPES_MAGASIN)} % local
          </span>
        </div>
      </div>

      {/* Totaux réseau */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ['Magasins', String(Object.values(parVille).flat().length), 'synthese-total-magasins'],
          ['Loyers + charges / mois', formatMoney(loyersMois), 'synthese-total-loyers'],
          ['Investis en améliorations', formatMoney(bilan.totalInvest), 'synthese-total-amels'],
          ['CA web cumulé', formatMoney(caWebCumul), 'synthese-ca-web'],
        ].map(([l, v, tid]) => (
          <div key={tid} className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-2" data-testid={tid}>
            <p className="text-[9px] uppercase tracking-wider text-slate-500">{l}</p>
            <p className="text-sm font-black font-mono text-foreground">{v}</p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2" data-testid="synthese-filtres">
        <Filter className="w-3.5 h-3.5 text-slate-500" />
        {TYPES_MAGASIN.map(t => {
          const actif = typesFiltre.includes(t.id);
          return (
            <button key={t.id} type="button" onClick={() => toggleType(t.id)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-bold transition-colors ${actif ? 'text-white shadow' : 'text-slate-400 border-ink-600 hover:border-ink-500'}`}
              style={actif ? { background: FLAG_TYPE_COLORS[t.id], borderColor: FLAG_TYPE_COLORS[t.id] } : {}}
              data-testid={`synthese-filtre-type-${t.id}`}>
              <span className="inline-block w-2 h-2 rounded-full border border-black/30" style={{ background: actif ? '#fff' : FLAG_TYPE_COLORS[t.id] }} />
              {t.nom}
              <span className={actif ? 'text-white/80' : 'text-slate-500'}>({nbParType[t.id] || 0})</span>
            </button>
          );
        })}
        <select value={paysFiltre} onChange={e => setPaysFiltre(e.target.value)}
          className="rounded-md bg-ink-800 border border-ink-600 text-[11px] text-foreground px-2 py-1"
          data-testid="synthese-filtre-pays">
          <option value="tous">🌍 Tous les pays</option>
          <option value="france">🇫🇷 France</option>
          <option value="intl">🏛️ International</option>
        </select>
        <select value={villeFiltre} onChange={e => setVilleFiltre(e.target.value)}
          className="rounded-md bg-ink-800 border border-ink-600 text-[11px] text-foreground px-2 py-1"
          data-testid="synthese-filtre-ville">
          <option value="toutes">Toutes les villes</option>
          {villesImplantees.map(v => <option key={v.id} value={v.id}>{v.drapeau} {v.nom}</option>)}
        </select>
        {(typesFiltre.length > 0 || paysFiltre !== 'tous' || villeFiltre !== 'toutes') && (
          <button type="button" onClick={() => { setTypesFiltre([]); setPaysFiltre('tous'); setVilleFiltre('toutes'); }}
            className="text-[10px] text-accent font-bold hover:underline" data-testid="synthese-filtre-reset">
            Réinitialiser
          </button>
        )}
      </div>

      {/* Tableau par ville */}
      <div className="surface-card bg-card border border-ink-600 rounded-xl overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-wider text-slate-500 border-b border-ink-600">
              <Th cle="ville">Ville</Th>
              <Th cle="statut">Statut</Th>
              <Th cle="nb">Magasins</Th>
              <Th cle="loyer" align="right">Loyer + charges</Th>
              <Th cle="contrib" align="right">Contribution trafic</Th>
              <th className="px-2 py-2 text-center">Améliorations</th>
              <th className="px-2 py-2">Événement</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(({ v, mags, types, st, loyer }) => {
              const statut = STATUTS_VILLE[st];
              const ev = (parVille[v.id] || []).map(x => evenementLocalActif(x.magasin, totalDays)).find(Boolean);
              return (
                <tr key={v.id} className="border-b border-ink-700/60 align-top" data-testid={`synthese-ville-${v.id}`}>
                  <td className="px-2 py-2 font-bold text-foreground whitespace-nowrap">{v.drapeau} {v.nom}<span className="block text-[9px] font-normal text-slate-500">{v.pays}</span></td>
                  <td className="px-2 py-2">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap" style={{ color: statut.couleur, borderColor: statut.couleur }}>
                      {statut.label}{types.length >= NB_TYPES_MAGASIN ? ' ★' : ''}
                    </span>
                    {bonusDominationPct(types.length) > 0 && (
                      <span className="block text-[9px] text-emerald-400 mt-0.5">+{bonusDominationPct(types.length)} % trafic local</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="space-y-1">
                      {mags.map(({ magasin: m, principal }) => (
                        <div key={m.id || 'principal'} className="flex items-center gap-1.5 rounded px-1.5 py-0.5"
                          style={{ background: `${FLAG_TYPE_COLORS[m.typeId] || '#94A3B8'}1A`, borderLeft: `3px solid ${FLAG_TYPE_COLORS[m.typeId] || '#94A3B8'}` }}>
                          <span className="font-bold" style={{ color: FLAG_TYPE_COLORS[m.typeId] || '#CBD5E1' }}>{getTypeMagasin(m.typeId)?.nom || 'Magasin'}</span>
                          <span className="text-slate-400 hidden sm:inline">{getLocalisation(m.localisationId)?.nom || ''}</span>
                          <span className="text-amber-300" title={getPositionnement(m.positionnement)?.nom}>{POS_BADGE[m.positionnement] || '◆'}</span>
                          {principal && <span className="text-[8px] font-bold text-amber-400">HQ</span>}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300 whitespace-nowrap">{formatMoney(loyer)}/m</td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300">
                    {mags.map(({ magasin: m, principal }) => (
                      <div key={m.id || 'principal'} className="leading-5">{principal ? 'siège' : `+${Math.round((contributions[m.id] || 0) * 100)} %`}</div>
                    ))}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {mags.map(({ magasin: m }) => {
                      const a = amelParMag[m.id || 'principal'];
                      return (
                        <div key={m.id || 'principal'} className="text-slate-300 flex items-center justify-center gap-1 leading-5" title={(a?.liste || []).join(', ') || 'Aucune amélioration'}>
                          <Wrench className="w-2.5 h-2.5 text-slate-500" />{a?.nb || 0}{a?.invest ? ` · ${formatMoney(a.invest)}` : ''}
                        </div>
                      );
                    })}
                  </td>
                  <td className="px-2 py-2">
                    {ev ? (
                      <span className={`text-[10px] font-bold ${ev.traficMult > 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {ev.emoji} {ev.nom} ({ev.traficMult > 1 ? '+' : ''}{Math.round((ev.traficMult - 1) * 100)} %)
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              );
            })}
            {lignes.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-500" data-testid="synthese-vide">
                {villesImplantees.length === 0 ? "Aucune implantation pour l'instant." : 'Aucune ville ne correspond à ces filtres.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-500">
        💡 Chaque ville accepte un magasin de chaque format ({NB_TYPES_MAGASIN} max). Réunir tous les formats dans une ville = ★ Domination (+{bonusDominationPct(NB_TYPES_MAGASIN)} % de trafic local). Couvrir le territoire augmente la notoriété de toute l'enseigne.
      </p>
    </div>
  );
}
